import { View, VStack, Text } from "@gluestack-ui/themed";
import { StyleSheet, useWindowDimensions } from "react-native";

const PORTRAIT_DOCUMENT_ASPECT_RATIO = 4 / 3;
const COMPACT_DOCUMENT_ASPECT_RATIO = 0.6;
const CAMERA_HEADER_HEIGHT = 64;
const CAMERA_CONTROLS_HEIGHT = 136;
const FRAME_WIDTH_RATIO = 0.92;
const COMPACT_FRAME_WIDTH_RATIO = 0.92;
const LANDSCAPE_FRAME_WIDTH_RATIO = 0.8;
const FRAME_HORIZONTAL_MARGIN = 12;
const FRAME_VERTICAL_MARGIN = 24;
const COMPACT_FRAME_VERTICAL_MARGIN = 8;
const COMPACT_CAMERA_TOP_MARGIN = 12;
const COMPACT_CONTROLS_GUTTER = 236;
const MIN_FRAME_HEIGHT = 160;
const LANDSCAPE_INSTRUCTION_HEIGHT = 54;
const LANDSCAPE_FRAME_HEIGHT_RATIO = 0.9;
const LANDSCAPE_SHUTTER_GUTTER = 116;
const LANDSCAPE_TOP_OFFSET = 12;

export const DocumentMask = () => {
  const { width: screenWidth, height: screenHeight } = useWindowDimensions();
  const isLandscape = screenWidth > screenHeight;
  const isCompactPortrait = !isLandscape && screenWidth < 768;
  const shutterGutter = isLandscape ? LANDSCAPE_SHUTTER_GUTTER : 0;
  const frameAreaWidth = screenWidth - shutterGutter;
  const landscapeTopOffset = isLandscape ? LANDSCAPE_TOP_OFFSET : 0;
  const verticalMargin = isCompactPortrait
    ? COMPACT_FRAME_VERTICAL_MARGIN
    : FRAME_VERTICAL_MARGIN;
  const availableHeight = Math.max(
    screenHeight - landscapeTopOffset - verticalMargin * 2,
    MIN_FRAME_HEIGHT
  );
  const portraitMaxHeight = Math.max(
    isCompactPortrait
      ? screenHeight - COMPACT_CAMERA_TOP_MARGIN - COMPACT_CONTROLS_GUTTER
      : screenHeight -
          CAMERA_HEADER_HEIGHT -
          CAMERA_CONTROLS_HEIGHT -
          FRAME_VERTICAL_MARGIN * 2,
    MIN_FRAME_HEIGHT
  );
  const maxFrameWidth = isLandscape
    ? frameAreaWidth - FRAME_HORIZONTAL_MARGIN * 2
    : isCompactPortrait
      ? frameAreaWidth - FRAME_HORIZONTAL_MARGIN * 2
      : screenWidth - FRAME_HORIZONTAL_MARGIN * 2;
  const portraitFrameWidth = Math.min(
    frameAreaWidth *
      (isCompactPortrait ? COMPACT_FRAME_WIDTH_RATIO : FRAME_WIDTH_RATIO),
    maxFrameWidth
  );
  const maskHeight = isLandscape
    ? availableHeight * LANDSCAPE_FRAME_HEIGHT_RATIO
    : Math.min(
        portraitFrameWidth /
          (isCompactPortrait
            ? COMPACT_DOCUMENT_ASPECT_RATIO
            : PORTRAIT_DOCUMENT_ASPECT_RATIO),
        portraitMaxHeight
      );
  const maskWidth = isLandscape
    ? Math.min(frameAreaWidth * LANDSCAPE_FRAME_WIDTH_RATIO, maxFrameWidth)
    : isCompactPortrait
      ? maskHeight * COMPACT_DOCUMENT_ASPECT_RATIO
      : portraitFrameWidth;
  const maskTop =
    (isLandscape
      ? landscapeTopOffset
      : isCompactPortrait
        ? COMPACT_CAMERA_TOP_MARGIN
        : CAMERA_HEADER_HEIGHT) +
    Math.max(
      isLandscape
        ? (availableHeight - maskHeight) / 2
        : (portraitMaxHeight - maskHeight) / 2,
      verticalMargin / 2
    );
  const instructionTop = isLandscape
    ? maskTop + maskHeight / 2 - LANDSCAPE_INSTRUCTION_HEIGHT / 2
    : undefined;

  return (
    <View style={styles.container} pointerEvents="none">
      {/* Middle section with transparent rectangle */}
      <View
        style={[
          styles.middleSection,
          {
            top: maskTop,
            height: maskHeight,
            right: shutterGutter
          }
        ]}
      >
        {/* Left overlay */}
        <View style={styles.sideOverlay} />

        {/* Center transparent area - document frame */}
        <View
          style={[
            styles.maskArea,
            {
              width: maskWidth,
              height: maskHeight
            }
          ]}
        />

        {/* Right overlay */}
        <View style={styles.sideOverlay} />
      </View>

      {/* Instructions */}
      <VStack
        position="absolute"
        top={instructionTop}
        bottom={isLandscape ? undefined : 120}
        left={0}
        right={shutterGutter}
        alignItems="center"
        px="$4"
      >
        <Text
          fontSize="$md"
          fontWeight={600}
          color="$white"
          textAlign="center"
          style={{
            textShadowColor: "rgba(0, 0, 0, 0.75)",
            textShadowOffset: { width: 0, height: 1 },
            textShadowRadius: 3
          }}
        >
          Posicione o documento dentro do quadro
        </Text>
        <Text
          fontSize="$sm"
          color="$white"
          textAlign="center"
          mt="$1"
          style={{
            textShadowColor: "rgba(0, 0, 0, 0.75)",
            textShadowOffset: { width: 0, height: 1 },
            textShadowRadius: 3
          }}
        >
          Certifique-se de que todas as informações estejam visíveis e legíveis
        </Text>
      </VStack>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 10
  },
  middleSection: {
    position: "absolute",
    left: 0,
    right: 0,
    flexDirection: "row"
  },
  sideOverlay: {
    flex: 1
  },
  maskArea: {
    borderRadius: 8,
    backgroundColor: "transparent",
    borderWidth: 3,
    borderColor: "rgba(255, 255, 255, 0.8)",
    alignSelf: "center"
  }
});
